import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getXeroBulkSsoUrl, openPopup } from '@/features/platform/utils'
import type { AdvisoryAiOrgRow } from '@/features/advisory-ai/types'

const POPUP_CLOSE_CHECK_MS = 1000
const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 120_000
/** A popup nobody ever closes should not leave a watcher running for the life of the page. */
const CLOSE_WATCH_TIMEOUT_MS = 10 * 60_000

type Options = {
  clientId: string
  userId: string
  envName: string
  /** The list this poll watches — the caller's own query key, filters and all. */
  orgsQueryKey: readonly unknown[]
}

export type XeroConnectResult = 'opened' | 'blocked' | 'no-user'

/**
 * Bulk SSO runs on its own origin and never returns to ours, so there is no
 * callback to hang the refetch off. The popup closing is the only signal we
 * get, and the connection rows land server-side a little after that — hence
 * the bounded poll rather than a single refetch.
 */
export function useXeroBulkSsoConnect({ clientId, userId, envName, orgsQueryKey }: Options) {
  const queryClient = useQueryClient()
  /**
   * Read through a ref so a filter change does not churn `open`, and so a poll
   * already running keeps comparing against the list it started on. Matching the
   * whole `aiOrgs` prefix instead would let a mid-poll filter change surface ids
   * absent from the baseline and read as a connection that never happened.
   */
  const orgsQueryKeyRef = useRef(orgsQueryKey)
  orgsQueryKeyRef.current = orgsQueryKey
  const popupRef = useRef<Window | null>(null)
  const closeCheckRef = useRef<number | null>(null)
  const pollRef = useRef<number | null>(null)
  const pollBusyRef = useRef(false)
  /**
   * Bumped by every start and stop. An in-flight refetch from a previous attempt
   * would otherwise resolve against a stale baseline and stop the poll that has
   * just replaced it — or call setState after unmount.
   */
  const generationRef = useRef(0)
  const [isPolling, setIsPolling] = useState(false)

  /**
   * Null when the query holds nothing at all, which is not the same as holding
   * an empty list: a client with no organisations yet has a perfectly good
   * baseline to compare against, and treating it as unknown would throw away
   * the one comparison that matters on a first connection.
   *
   * Ids are validated rather than asserted — getQueryData cannot check the
   * shape, and a row without an id would put `undefined` in the set.
   */
  const cachedOrgIds = useCallback(
    (key: readonly unknown[]): Set<string> | null => {
      const rows = queryClient.getQueryData<AdvisoryAiOrgRow[]>(key)
      if (!rows) return null
      const ids = new Set<string>()
      rows.forEach((row) => {
        if (typeof row?.id === 'string') ids.add(row.id)
      })
      return ids
    },
    [queryClient]
  )

  const stopCloseCheck = useCallback(() => {
    if (closeCheckRef.current != null) window.clearInterval(closeCheckRef.current)
    closeCheckRef.current = null
  }, [])

  const stopPolling = useCallback(() => {
    generationRef.current += 1
    if (pollRef.current != null) window.clearInterval(pollRef.current)
    pollRef.current = null
    pollBusyRef.current = false
    setIsPolling(false)
  }, [])

  useEffect(
    () => () => {
      generationRef.current += 1
      if (closeCheckRef.current != null) window.clearInterval(closeCheckRef.current)
      if (pollRef.current != null) window.clearInterval(pollRef.current)
    },
    []
  )

  /**
   * `known` is what the list held when the user set off to connect. It has to be
   * captured then and not on the first tick: by the time a tick has run, the new
   * organisation is already in the list and would be absorbed into the baseline,
   * leaving nothing to detect. Falling back to the first tick is only for the
   * case where the list had not loaded at all and there is genuinely nothing to
   * compare against.
   */
  const startPolling = useCallback(
    (key: readonly unknown[], clickBaseline: Set<string> | null) => {
      stopPolling()
      const generation = (generationRef.current += 1)
      setIsPolling(true)

      const startedAt = Date.now()
      let known = clickBaseline

      pollRef.current = window.setInterval(() => {
        // The cap is enforced here, ahead of the busy guard, so it holds however
        // a refetch behaves. Checked inside the callback instead, a request that
        // never settles would leave `pollBusyRef` set, turn every later tick into
        // an early return, and run the interval for the life of the page.
        if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
          stopPolling()
          return
        }
        if (pollBusyRef.current) return

        pollBusyRef.current = true
        void queryClient
          .refetchQueries({ queryKey: key, exact: true })
          .then(() => {
            if (generationRef.current !== generation) return
            const ids = cachedOrgIds(key)
            // Nothing to read back — no comparison to make, and nothing worth
            // recording as a baseline.
            if (!ids) return
            const seen = known
            if (!seen) {
              known = ids
              return
            }
            if ([...ids].some((id) => !seen.has(id))) stopPolling()
          })
          // refetchQueries swallows query errors unless `throwOnError` is set, so
          // this only guards against that default changing under us.
          .catch(() => undefined)
          .finally(() => {
            if (generationRef.current === generation) pollBusyRef.current = false
          })
      }, POLL_INTERVAL_MS)
    },
    [cachedOrgIds, queryClient, stopPolling]
  )

  const open = useCallback((): XeroConnectResult => {
    if (!userId) return 'no-user'

    const popup = openPopup(getXeroBulkSsoUrl(clientId, userId, envName), 600, 500)
    if (!popup) return 'blocked'

    popupRef.current = popup
    // Reconnecting mid-poll restarts the watch rather than leaving the previous
    // one running against a baseline that predates this attempt.
    stopCloseCheck()
    stopPolling()

    const pollKey = orgsQueryKeyRef.current
    const baseline = cachedOrgIds(pollKey)
    const watchStartedAt = Date.now()
    closeCheckRef.current = window.setInterval(() => {
      const stillOpen = popupRef.current != null && !popupRef.current.closed
      if (stillOpen && Date.now() - watchStartedAt < CLOSE_WATCH_TIMEOUT_MS) return
      stopCloseCheck()
      const abandoned = stillOpen
      popupRef.current = null
      if (!abandoned) startPolling(pollKey, baseline)
    }, POPUP_CLOSE_CHECK_MS)

    return 'opened'
  }, [cachedOrgIds, clientId, envName, startPolling, stopCloseCheck, stopPolling, userId])

  return { open, isPolling }
}
